import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Landmark, CheckCheck, Loader2, History, Paperclip, X } from "lucide-react";
import { TransfersDialog, uploadReceipt, RECEIPT_ACCEPT } from "@/components/PayoutReceipts";

const money = (v) => `${Number(v || 0).toFixed(2)} ر.س`;
const fmt = (s) => (s ? new Date(s).toLocaleDateString("ar-SA-u-nu-latn", { dateStyle: "medium" }) : "—");

export function AdminPayouts() {
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [histId, setHistId] = useState(null);

  const load = () => api.get("/admin/payouts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    setBusy(true);
    try {
      const receipt_file_id = file ? await uploadReceipt(file) : null;
      const { data } = await api.post(`/admin/payouts/${target.payee_id}/transfer`, { note, receipt_file_id });
      toast.success(`تم تسجيل تحويل ${money(data.amount)} إلى ${target.name}`);
      setTarget(null); setNote(""); setFile(null); load();
    } catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); }
    setBusy(false);
  };

  const pendingTotal = rows.reduce((s, r) => s + r.pending_net, 0);
  return (
    <Card className="p-5 space-y-4" data-testid="admin-payouts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold flex items-center gap-2"><Landmark className="w-5 h-5 text-primary" /> كشف مستحقات الشركاء</h3>
        <div className="text-sm">بانتظار التحويل: <span className="font-extrabold text-amber-700" data-testid="payouts-pending-total">{money(pendingTotal)}</span></div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {["الشريك", "العمليات", "إجمالي المدفوع", "العمولة", "الصافي", "تم تحويله", "بانتظار التحويل", "آخر تحويل", ""].map((h, i) => <TableHead key={i} className="text-start whitespace-nowrap">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.payee_id} data-testid={`payout-row-${r.payee_id}`}>
                <TableCell><div className="font-semibold text-sm">{r.name}</div><div className="text-xs text-muted-foreground">{r.role === "workshop" ? "ورشة" : "سطحة"}{r.phone && <> · <bdi dir="ltr">{r.phone}</bdi></>}</div>
                  {r.bank
                    ? <div className="text-xs mt-1" data-testid={`payout-bank-${r.payee_id}`}>{r.bank.bank_name} · <bdi dir="ltr" className="font-mono">{r.bank.iban}</bdi></div>
                    : <div className="text-xs mt-1 text-red-600" data-testid={`payout-bank-missing-${r.payee_id}`}>لم يضف الحساب البنكي</div>}
                </TableCell>
                <TableCell>{r.count}</TableCell>
                <TableCell className="whitespace-nowrap">{money(r.gross)}</TableCell>
                <TableCell className="whitespace-nowrap text-emerald-700">{money(r.commission)}</TableCell>
                <TableCell className="whitespace-nowrap font-semibold">{money(r.net)}</TableCell>
                <TableCell className="whitespace-nowrap">{money(r.transferred_net)}</TableCell>
                <TableCell className="whitespace-nowrap font-bold text-amber-700" data-testid={`payout-pending-${r.payee_id}`}>{money(r.pending_net)}</TableCell>
                <TableCell className="whitespace-nowrap text-sm" data-testid={`payout-last-${r.payee_id}`}>{fmt(r.last_transfer_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                  {r.pending_count > 0 ? (
                    <Button size="sm" className="rounded-full gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setTarget(r)} data-testid={`payout-transfer-${r.payee_id}`}>
                      <CheckCheck className="w-4 h-4" /> تم التحويل
                    </Button>
                  ) : <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">محوّل بالكامل</Badge>}
                  {r.transfers?.length > 0 && (
                    <Button size="sm" variant="ghost" className="gap-1" onClick={() => setHistId(r.payee_id)} data-testid={`payout-history-${r.payee_id}`}>
                      <History className="w-4 h-4" /> التحويلات
                    </Button>
                  )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8" data-testid="payouts-empty">لا توجد مستحقات بعد</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent dir="rtl" data-testid="payout-dialog">
          <DialogHeader><DialogTitle>تأكيد التحويل — {target?.name}</DialogTitle></DialogHeader>
          <p className="text-sm">سيتم تسجيل تحويل <b>{money(target?.pending_net)}</b> عن {target?.pending_count} عملية بتاريخ اليوم، ويصل للشريك إشعار بذلك.</p>
          {target?.bank ? (
            <div className="rounded-xl bg-muted/40 p-3 text-sm space-y-1" data-testid="payout-dialog-bank">
              <div>البنك: <b>{target.bank.bank_name}</b></div>
              <div>صاحب الحساب: <b>{target.bank.account_holder}</b></div>
              <div className="flex items-center gap-2">الآيبان: <bdi dir="ltr" className="font-mono font-bold select-all">{target.bank.iban}</bdi>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { navigator.clipboard.writeText(target.bank.iban); toast.success("تم نسخ الآيبان"); }} data-testid="payout-copy-iban">نسخ</Button>
              </div>
            </div>
          ) : <p className="text-sm text-red-600" data-testid="payout-dialog-no-bank">لم يضف هذا الشريك حسابه البنكي بعد.</p>}
          <p className="text-xs text-muted-foreground">إذا كان الصافي 2000 ريال أو أقل تُخصم الرسوم البنكية من المبلغ المحوّل حسب العقد.</p>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="رقم الحوالة أو ملاحظة (اختياري)" data-testid="payout-note-input" />
          <label className="flex items-center gap-2 rounded-xl border border-dashed border-border p-3 text-sm cursor-pointer hover:bg-muted/40" data-testid="payout-receipt-picker">
            <Paperclip className="w-4 h-4 text-primary" />
            <span className="flex-1 truncate">{file ? file.name : "إرفاق إيصال الحوالة — صورة أو PDF (اختياري)"}</span>
            {file && <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }} data-testid="payout-receipt-clear"><X className="w-4 h-4" /></button>}
            <input type="file" accept={RECEIPT_ACCEPT} hidden onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="payout-receipt-input" />
          </label>
          <DialogFooter>
            <Button className="w-full rounded-xl gap-2" disabled={busy} onClick={confirm} data-testid="payout-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} تأكيد التحويل</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TransfersDialog partner={rows.find((r) => r.payee_id === histId)} onClose={() => setHistId(null)} onChanged={load} />
    </Card>
  );
}
